import {useCallback, useContext, useMemo} from 'react';

import {MethodEditorTab} from 'procedures/design/method-config/MethodConfigConstants';
import useOnMount from 'ui-library/hooks/useOnMount';
import {FlowchartSidepanelManagerContext} from 'ui-library/reactflow/sidepanels/sidepanel-manager/FlowchartSidepanelUnsavedChangesContext';

const METHOD_EDITOR_SIDEPANEL_ID = 'method-editor';
/**
 * Hook to extract sidepanel manager state for MethodEditor in both PoW and legacy procedure editors.
 *
 * When used in the legacy procedure editor, effectively a no-op and calls legacySetCurrentTab directly.
 *
 * When used in the PoW procedure editor, the MethodEditor is rendered as a tab in a sidepanel.
 * This hook manages changing the tabs within the MethodEditor as "nested tabs" for the flowchart sidepanel manager.
 */
export default function useMethodEditorSidepanelPoW(
  currentTab: MethodEditorTab,
  legacySetCurrentTab: (newTab: MethodEditorTab) => void
): {
  activeMethodEditorTab: MethodEditorTab;
  changeToTab: (newTab: MethodEditorTab) => Promise<void>;
} {
  const sidepanelManager = useContext(FlowchartSidepanelManagerContext);
  const effectiveState = sidepanelManager?.getEffectiveState();

  const sidepanelState = useMemo(
    () => ({
      sidepanelManager: sidepanelManager ?? null,
      methodEditorMounted: effectiveState?.activeTabState?.activeTabId != null,
      activeMethodEditorTab:
        (effectiveState?.activeTabState?.nestedActiveTabId as MethodEditorTab) ?? currentTab,
    }),
    [
      sidepanelManager,
      effectiveState?.activeTabState?.activeTabId,
      effectiveState?.activeTabState?.nestedActiveTabId,
      currentTab,
    ]
  );

  useOnMount(() => {
    sidepanelManager?.changeTab({activeTabId: METHOD_EDITOR_SIDEPANEL_ID, nestedActiveTabId: currentTab});
  });

  const changeToTab = useCallback(
    async (newTab: MethodEditorTab): Promise<void> => {
      if (sidepanelManager != null && sidepanelState.methodEditorMounted === true) {
        await sidepanelManager.changeTab({
          activeTabId: METHOD_EDITOR_SIDEPANEL_ID,
          nestedActiveTabId: newTab,
        });
      } else if (sidepanelManager == null) {
        // Fallback if no sidepanel manager (legacy procedure editor)
        legacySetCurrentTab(newTab);
      }
    },
    [sidepanelManager, sidepanelState.methodEditorMounted, legacySetCurrentTab]
  );

  return {
    activeMethodEditorTab: sidepanelState.activeMethodEditorTab,
    changeToTab,
  };
}
